import {
  AnchorHTMLAttributes,
  ButtonHTMLAttributes,
  Children,
  CSSProperties,
  forwardRef,
  Ref,
} from "react";
import styled from "@emotion/styled";
import {
  AnyRouter,
  createLink,
  CreateLinkProps,
  LinkComponentProps,
  RegisteredRouter,
  ToPathOption,
} from "@tanstack/react-router";
import { Text } from "styles/utility-components";

import UIcon, { IconProps } from "lib/icons/untitled_ui/UIcon";

export type ButtonType = "primary" | "secondary" | "tertiary";
export type ButtonStyle = "default" | "destructive" | "inverse";
type ButtonElementTypes = "button" | "a" | undefined; // undefined = Button
type ButtonShape = "circle" | "default";

type AnchorSpecificProps = {
  as: "a";
  href: string;
};
type ButtonSpecificProps = {
  as?: "button";
};

/**
 * Since we aren't using pointer-events: none when disabled as we have cursor: not-allowed instead,
 * we need to handle disabled events ourself
 */
const disabledOnWrapper = (
  e: React.MouseEvent,
  disabled: boolean = false,
  originalFunc: React.MouseEventHandler | undefined,
) => {
  if (disabled) {
    e.preventDefault();
    e.stopPropagation();
  } else if (originalFunc) {
    originalFunc(e);
  }
};

/**
 * Returns true if all children are plain text (string or number), false otherwise
 *
 * Only exported for testing purposes
 */
export const checkIfPlainText = (children: React.ReactNode): boolean => {
  // Children.toArray filters out empty nodes (null, undefined & booleans) for us and converts single children into an array
  // so we can handle both cases uniformly https://react.dev/reference/react/Children#children-toarray-caveats
  const childrenArray = Children.toArray(children);

  if (childrenArray.length === 0) return false; // No nodes with content

  return childrenArray.every((child) => {
    const type = typeof child;
    return (type === "string" && child !== "") || type === "number";
  });
};

type ButtonStylingProps = {
  children?: React.ReactNode;
  icon?: IconProps["icon"];
  iconProps?: Omit<IconProps, "icon">;
  buttonType?: ButtonType;
  buttonStyle?: ButtonStyle;
  blackText?: boolean;
  fullWidth?: boolean;
  justifyContent?: CSSProperties["justifyContent"];
  shape?: ButtonShape;
  disabled?: boolean;
  loading?: boolean;
  width?: number | string;
  maxWidth?: number | string;
  height?: number | string;
  /**
   * Lets a long label wrap onto a second line. The button keeps its normal 32px height as a
   * minimum and grows downwards, rather than the text spilling out past the bottom border.
   */
  multiline?: boolean;
};

export type ButtonProps<T extends ButtonElementTypes> = ButtonStylingProps & {
  as?: T;
} & (T extends "a"
    ? AnchorHTMLAttributes<HTMLAnchorElement> & AnchorSpecificProps
    : ButtonHTMLAttributes<HTMLButtonElement> & ButtonSpecificProps);

const ButtonInternal = <T extends ButtonElementTypes = "button">(
  {
    as,
    children,
    icon,
    iconProps,
    buttonType = "primary",
    buttonStyle = "default",
    blackText = false,
    disabled,
    loading,
    multiline,
    ...rest
  }: ButtonProps<T>,
  ref: Ref<T extends "a" ? HTMLAnchorElement : HTMLButtonElement>,
) => {
  if (buttonType === "secondary") {
    iconProps = { color: "sysPrimaryDefault", ...iconProps };
  }

  const isDisabled = disabled || loading;
  const isLink = as === "a";
  const href = isLink ? (rest as unknown as AnchorSpecificProps).href : undefined;

  const anchorRest = isLink ? (rest as AnchorHTMLAttributes<HTMLAnchorElement>) : undefined;
  const rel =
    anchorRest?.target === "_blank" ? anchorRest.rel ?? "noopener noreferrer" : anchorRest?.rel;

  const iconKey = loading ? "spinner" : icon;
  const useTextWrapper = checkIfPlainText(children);
  const Wrapper = multiline ? MultilineButtonWrapper : ButtonWrapper;

  return (
    <Wrapper
      as={as}
      buttonType={buttonType}
      buttonStyle={buttonStyle}
      data-stotles-button
      {...(as !== "a" && {
        type: "button", // Default is submit which we don't want
      })}
      {...(rest as ButtonProps<"button" | undefined>)} // TypeScript doesn't like spreading generic types
      ref={ref as Ref<HTMLButtonElement>}
      href={href}
      rel={rel}
      // Below is all related to disabled
      disabled={isLink ? undefined : isDisabled} // Prevent disabled from being set on anchor elements (not a valid attribute)
      aria-disabled={isDisabled}
      tabIndex={isDisabled ? -1 : rest.tabIndex} // Prevents focus on disabled links
      // Add more events below as needed
      onClick={(e) => disabledOnWrapper(e, isDisabled, rest.onClick)}
      onPointerDown={(e) => disabledOnWrapper(e, isDisabled, rest.onPointerDown)}
    >
      {iconKey && <UIcon icon={iconKey} size={16} {...iconProps} className="icon" />}
      {/* The ellipsis clamp is white-space: nowrap, so it has to be off when the label may wrap */}
      {useTextWrapper ? (
        <Text color={blackText ? "sysTextDefault" : "inherit"} ellipsis={!multiline}>
          {children}
        </Text>
      ) : (
        children
      )}
    </Wrapper>
  );
};

const ButtonWrapper = styled.button<{
  buttonType: ButtonType;
  buttonStyle: ButtonStyle;
  shape?: ButtonShape;
  fullWidth?: boolean;
  width?: number | string;
  maxWidth?: number | string;
  height?: number | string;
  justifyContent?: CSSProperties["justifyContent"];
  // Below are added to prevent TypeScript errors from spreading props
  loading?: boolean;
  href?: string;
}>(
  ({
    theme,
    buttonType,
    buttonStyle,
    shape,
    fullWidth,
    justifyContent,
    width,
    maxWidth,
    height,
  }) => ({
    all: "unset",
    height: height ?? 32,
    ...(shape === "circle" && { width: 32 }),
    ...(width && { width }),
    ...(maxWidth && { maxWidth }),
    borderRadius: shape === "circle" ? "50%" : 4,
    transition: "background-color 0.2s, border-color 0.2s",
    display: "flex",
    flexGrow: fullWidth ? 1 : 0,
    alignItems: "center",
    justifyContent: justifyContent ?? "center",
    gap: 8,
    cursor: "pointer",
    boxSizing: "border-box",

    padding: "0 12px",
    "&:has(> .icon:only-child)": {
      // Less padding if button only contains an icon. Not 8px due to 1px border
      // Need to be careful that button doesn't get a direct text node since they don't count as children (e.g. via <>test</>)
      // https://developer.mozilla.org/en-US/docs/Web/CSS/Reference/Selectors/:-moz-first-node is the correct selector
      // but is only supported in Firefox currently, see https://github.com/w3c/csswg-drafts/issues/3216 for more info
      padding: "0 7px",
    },

    ...(buttonType === "primary" && {
      backgroundColor: theme.colors.sysPrimaryDefault,
      color: theme.colors.sysTextWhite,
      border: "1px solid transparent", // Have so buttons all are the same height & width
      "&:hover, &[data-state='open']:not([data-ignore-state-styles])": {
        color: theme.colors.sysTextWhite,
        backgroundColor: theme.colors.sysPrimaryHover,
      },
      // "active" classname can be applied to force active state
      "&:active, &.active": {
        backgroundColor: theme.colors.sysPrimaryPressed,
      },
      "&:focus-visible": {
        outline: `2px solid ${theme.colors.sysPrimarySubtleAlt}`,
      },
      "&[aria-disabled='true']": {
        cursor: "not-allowed",
        backgroundColor: theme.colors.sysPrimarySubtleAlt,
      },

      ...(buttonStyle === "destructive" && {
        backgroundColor: theme.colors.sysDestructiveDefault,
        "&:hover, &[data-state='open']:not([data-ignore-state-styles])": {
          color: theme.colors.sysTextWhite,
          backgroundColor: theme.colors.sysDestructiveHover,
        },
        "&:active, &.active": {
          backgroundColor: theme.colors.sysDestructivePressed,
        },
        "&:focus-visible": {
          outline: `2px solid ${theme.colors.sysDestructiveSubtleAlt}`,
        },
        "&[aria-disabled='true']": {
          cursor: "not-allowed",
          backgroundColor: theme.colors.sysDestructiveSubtleAlt,
        },
      }),

      ...(buttonStyle === "inverse" && {
        backgroundColor: theme.colors.sysBackgroundDefault,
        color: theme.colors.sysTextDefault,
        "&:hover, &[data-state='open']:not([data-ignore-state-styles])": {
          color: theme.colors.sysTextDefault,
          backgroundColor: theme.colors.sysBackgroundAlternative,
        },
        "&:active, &.active": {
          backgroundColor: theme.colors.sysBackgroundInactive,
        },
        "&:focus-visible": {
          outline: `2px solid ${theme.colors.sysPrimarySubtleAlt}`,
        },
        "&[aria-disabled='true']": {
          cursor: "not-allowed",
          color: theme.colors.sysTextInactive,
          backgroundColor: theme.colors.sysBackgroundDefault,
        },
      }),
    }),

    ...(buttonType === "secondary" && {
      backgroundColor: theme.colors.sysBackgroundDefault,
      color: theme.colors.sysTextDefault,
      border: `1px solid ${theme.colors.sysBorderDecorative}`,
      "&:hover, &[data-state='open']:not([data-ignore-state-styles])": {
        color: theme.colors.sysTextDefault,
        backgroundColor: theme.colors.sysPrimarySubtle,
        borderColor: theme.colors.sysPrimaryHover,
      },
      "&:active, &.active": {
        backgroundColor: theme.colors.sysPrimarySubtleAlt,
        borderColor: theme.colors.sysPrimaryPressed,
      },
      "&:focus-visible": {
        outline: `2px solid ${theme.colors.sysBorderDecorative}`,
      },
      "&[aria-disabled='true']": {
        cursor: "not-allowed",
        backgroundColor: theme.colors.sysBackgroundInactive,
        color: theme.colors.sysTextInactive,
        borderColor: theme.colors.sysBorderDecorative,
        "& .icon": {
          color: theme.colors.sysTextInactive,
        },
      },

      ...(buttonStyle === "destructive" && {
        borderColor: theme.colors.sysDestructiveDefault,
        "&:hover, &[data-state='open']:not([data-ignore-state-styles])": {
          color: theme.colors.sysTextDefault,
          borderColor: theme.colors.sysDestructiveHover,
          backgroundColor: theme.colors.sysDestructiveSubtle,
          "& .icon": {
            color: theme.colors.sysDestructiveHover,
          },
        },
        "&:active, &.active": {
          borderColor: theme.colors.sysDestructivePressed,
          backgroundColor: theme.colors.sysDestructiveSubtleAlt,
          "& .icon": {
            color: theme.colors.sysDestructivePressed,
          },
        },
        "&:focus-visible": {
          outline: `2px solid ${theme.colors.sysDestructiveSubtleAlt}`,
        },
        "&[aria-disabled='true']": {
          cursor: "not-allowed",
          backgroundColor: theme.colors.sysBackgroundInactive,
          borderColor: theme.colors.sysBorderDecorative,
          color: theme.colors.sysTextInactive,
          "& .icon": {
            color: theme.colors.sysTextInactive,
          },
        },
      }),

      ...(buttonStyle === "inverse" && {
        borderColor: theme.colors.sysBorderDecorative,
        backgroundColor: "transparent",
        color: theme.colors.sysTextWhite,
        "&:hover, &[data-state='open']:not([data-ignore-state-styles])": {
          color: theme.colors.sysTextWhite,
          borderColor: theme.colors.sysTextWhite,
          backgroundColor: "rgba(255, 255, 255, 0.08)",
        },
        "&:active, &.active": {
          borderColor: theme.colors.sysTextWhite,
          backgroundColor: "rgba(255, 255, 255, 0.12)",
        },
        "&:focus-visible": {
          outline: `2px solid ${theme.colors.sysPrimarySubtle}`,
        },
        "&[aria-disabled='true']": {
          cursor: "not-allowed",
          backgroundColor: "transparent",
          borderColor: theme.colors.sysTextInactive,
          color: theme.colors.sysTextInactive,
          "& .icon": {
            color: theme.colors.sysTextInactive,
          },
        },
      }),
    }),

    ...(buttonType === "tertiary" && {
      backgroundColor: "transparent",
      color: theme.colors.sysPrimaryDefault,
      border: "1px solid transparent", // Have so buttons all are the same height
      "&:hover, &[data-state='open']:not([data-ignore-state-styles])": {
        color: theme.colors.sysPrimaryDefault,
        backgroundColor: theme.colors.sysPrimarySubtle,
      },
      "&:active, &.active": {
        backgroundColor: theme.colors.sysPrimarySubtleAlt,
      },
      "&:focus-visible": {
        outline: `2px solid ${theme.colors.sysPrimarySubtle}`,
      },
      "&[aria-disabled='true']": {
        cursor: "not-allowed",
        color: theme.colors.sysTextInactive,
      },

      ...(buttonStyle === "destructive" && {
        color: theme.colors.sysDestructiveDefault,
        "&:hover, &[data-state='open']:not([data-ignore-state-styles])": {
          color: theme.colors.sysDestructiveDefault,
          backgroundColor: theme.colors.sysDestructiveSubtle,
        },
        "&:active, &.active": {
          backgroundColor: theme.colors.sysDestructiveSubtleAlt,
        },
        "&:focus-visible": {
          outline: `2px solid ${theme.colors.sysDestructiveSubtleAlt}`,
        },
        "&[aria-disabled='true']": {
          cursor: "not-allowed",
          color: theme.colors.sysTextInactive,
        },
      }),

      ...(buttonStyle === "inverse" && {
        color: theme.colors.sysTextWhite,
        "&:hover, &[data-state='open']:not([data-ignore-state-styles])": {
          color: theme.colors.sysTextWhite,
          backgroundColor: "rgba(255, 255, 255, 0.08)",
        },
        "&:active, &.active": {
          backgroundColor: "rgba(255, 255, 255, 0.12)",
        },
        "&:focus-visible": {
          outline: `2px solid ${theme.colors.sysPrimarySubtle}`,
        },
        "&[aria-disabled='true']": {
          cursor: "not-allowed",
          color: theme.colors.sysTextInactive,
        },
      }),
    }),
  }),
);

/**
 * Opt-in variant for buttons whose label can wrap. Composed on top of ButtonWrapper so its
 * class is emitted later and wins the cascade; `height: auto` alone would shrink the button
 * below the 32px used by sibling inputs, so the 32px is kept as a floor.
 */
const MultilineButtonWrapper = styled(ButtonWrapper)({
  height: "auto",
  minHeight: 32,
  // Matches the clearance a single line gets inside the fixed 32px height, so a wrapped
  // second line is not flush against the border.
  padding: "4px 12px",
});

ButtonInternal.displayName = ButtonWrapper.displayName;

export type AnchorButtonProps = ButtonStylingProps & {
  as: "a";
  href: string;
  to?: never;
} & Omit<AnchorHTMLAttributes<HTMLAnchorElement>, "type" | "href">;

export type ButtonButtonProps = ButtonStylingProps & {
  as?: "button";
} & ButtonHTMLAttributes<HTMLButtonElement>;

export type RouterAnchorButtonProps<
  TRouter extends AnyRouter = RegisteredRouter,
  TFrom extends string = string,
  TTo extends string = string,
  TMaskFrom extends string = TFrom,
  TMaskTo extends string = "",
> = ButtonStylingProps & {
  as: "a";
  href?: never;
  to: ToPathOption<TRouter, TFrom, TTo> & {};
} & Omit<
    LinkComponentProps<"a", TRouter, TFrom, TTo, TMaskFrom, TMaskTo>,
    "type" | "children" | "to"
  >;

export type CombinedButtonProps = {
  <
    TRouter extends AnyRouter = RegisteredRouter,
    const TFrom extends string = string,
    const TTo extends string = string,
    const TMaskFrom extends string = TFrom,
    const TMaskTo extends string = "",
  >(
    props: RouterAnchorButtonProps<TRouter, TFrom, TTo, TMaskFrom, TMaskTo> & {
      ref?: React.Ref<HTMLAnchorElement>;
    },
  ): JSX.Element;
  (
    props: AnchorButtonProps & {
      ref?: React.Ref<HTMLAnchorElement>;
    },
  ): JSX.Element;
  (
    props: ButtonButtonProps & {
      ref?: React.Ref<HTMLButtonElement>;
    },
  ): JSX.Element;
};

const ButtonInternalOuter = forwardRef(ButtonInternal);

// createLink wraps ButtonInternalOuter so TanStack Router props are resolved to
// anchor props (for example, href and onClick) before reaching ButtonInternal.
// The cast is confined to this line because ButtonInternalOuter's overloaded
// signatures do not directly unify with createLink's
// `Constrain<TComp, any, (props: CreateLinkProps) => ReactNode>`.
const RouterLinkButton = createLink(
  ButtonInternalOuter as unknown as (props: CreateLinkProps) => React.ReactNode,
);

// Excluded RouterAnchorButtonProps to keep DispatcherProps cheap — its generics
// (LinkComponentProps) would force TanStack's heavy type tree through this
// internal union for no benefit, since the router branch casts to CreateLinkProps
// and consumer-facing typing lives in CombinedButtonProps.
type DispatcherProps = AnchorButtonProps | ButtonButtonProps;

const ButtonDispatcher = forwardRef<HTMLAnchorElement | HTMLButtonElement, DispatcherProps>(
  (props, ref) => {
    const maybeTo = (props as { to?: unknown }).to;
    if (props.as === "a" && maybeTo !== undefined) {
      // If it's a tanstack router link, render the createLink wrapper which will resolve the to prop into href and onClick for us
      // Other options require a conditional hook which is IMO worse than these casts

      return (
        <RouterLinkButton
          {...(props as unknown as CreateLinkProps)}
          // TODO: When we upgrade to React 19, we should hopefully be able to get rid of the awful cast below
          // TanStack's Link runs useForwardedRef internally, so the user-supplied ref lands on the anchor DOM node
          // (verified in the "forwards ref to the underlying anchor on the router link path" test).
          ref={ref as unknown as never}
        />
      );
    }

    // If it's not a tanstack router link, render the internal button directly (both anchor and button variants)
    return <ButtonInternalOuter {...props} ref={ref} />;
  },
);
ButtonDispatcher.displayName = "ButtonDispatcher";

/**
 *
 * @param as Which element to render this button as (button, a)
 * @param children Text or JSX to render inside the button
 * @param icon Untitled UI icon name to render inside the button
 * @param iconProps Props to pass to the icon (color, size, fill, etc.)
 * @param buttonType The type of button to render (primary, secondary, tertiary)
 * @param buttonStyle The style of button to render (default, destructive, inverse)
 * @param blackText Whether the text should be forced to black
 * @param fullWidth Whether the button should take the full width of its container
 * @param justifyContent The justify content of the button
 * @param shape The shape of the button (circle, default)
 * @param disabled Whether the button is disabled
 * @param loading Whether the button is loading
 * @param href If as="a" without TanStack router props, the href to render
 * @param to If as="a", the TanStack Router destination — also accepts `from`, `params`, `search`, etc.
 */
const Button = ButtonDispatcher as unknown as CombinedButtonProps;

export default Button;
